import type { InjectionKey } from 'vue';
import { createTokenApi } from './token-api';
import { createScannerApi } from './scanner-api';
import { createProductsApi } from './products-api';
import { createCheckoutApi } from './checkout-api';
import { createPointsApi } from './points-api';
import { createScansApi } from './scans-api';
import { createOrdersApi } from './orders-api';
import { createLoyaltyApi } from './loyalty-api';
import { createVouchersApi } from './vouchers-api';
import { createSweepstakeApi } from './sweepstake-api';
import { klErrorParser } from '~/api/client-only/helpers/kl-error-parser';

export const scannerApiKey: InjectionKey<ReturnType<typeof createScannerApi>> = Symbol('scannerApi');
export const productsApiKey: InjectionKey<ReturnType<typeof createProductsApi>> = Symbol('productsApi');
export const checkoutApiKey: InjectionKey<ReturnType<typeof createCheckoutApi>> = Symbol('checkoutApi');
export const pointsApiKey: InjectionKey<ReturnType<typeof createPointsApi>> = Symbol('pointsApi');
export const scansApiKey: InjectionKey<ReturnType<typeof createScansApi>> = Symbol('scansApi');
export const ordersApiKey: InjectionKey<ReturnType<typeof createOrdersApi>> = Symbol('ordersApi');
export const loyaltyApiKey: InjectionKey<ReturnType<typeof createLoyaltyApi>> = Symbol('loyaltyApi');
export const vouchersApiKey: InjectionKey<ReturnType<typeof createVouchersApi>> = Symbol('vouchersApi');
export const sweepstakeApiKey: InjectionKey<ReturnType<typeof createSweepstakeApi>> = Symbol('sweepstakesApi');

/**
 * Provides api's to be used within pages of the application.
 * Note; This hook does not provide the cmsPageApi, because that should only be used on page-level.
 * @param baseUrl Base url of the api we are calling (Krasloten backend)
 */
export default function useClientApi(baseUrl: string) {
	const app = useNuxtApp();
	const { getXSRFToken: tokenApiCall } = createTokenApi(getNloFetcherConfig({ baseUrl, errorParser: klErrorParser }));
	const fetchConfig = getNloFetcherConfig({
		baseUrl,
		tokenApiCall,
		errorParser: klErrorParser,
	});

	app.vueApp.provide(scannerApiKey, createScannerApi(fetchConfig));
	app.vueApp.provide(productsApiKey, createProductsApi(fetchConfig));
	app.vueApp.provide(checkoutApiKey, createCheckoutApi(fetchConfig));
	app.vueApp.provide(pointsApiKey, createPointsApi(fetchConfig));
	app.vueApp.provide(scansApiKey, createScansApi(fetchConfig));
	app.vueApp.provide(ordersApiKey, createOrdersApi(fetchConfig));
	app.vueApp.provide(loyaltyApiKey, createLoyaltyApi(fetchConfig));
	app.vueApp.provide(vouchersApiKey, createVouchersApi(fetchConfig));
	app.vueApp.provide(sweepstakeApiKey, createSweepstakeApi(fetchConfig));
}

/**
 * Returns injected scanner api
 * If there is no api provided, an error will be thrown.
 */
export function useScannerApi() {
	const api = inject(scannerApiKey);

	if (!api) {
		throw new Error('Scanner api not provided');
	}
	return api;
}

export function useProductsApi() {
	const api = inject(productsApiKey);

	if (!api) {
		throw new Error('Products api not provided');
	}
	return api;
}

export function useCheckoutApi() {
	const api = inject(checkoutApiKey);

	if (!api) {
		throw new Error('Checkout api not provided');
	}
	return api;
}

export function usePointsApi() {
	const api = inject(pointsApiKey);

	if (!api) {
		throw new Error('Points api not provided');
	}
	return api;
}

export function useScansApi() {
	const api = inject(scansApiKey);

	if (!api) {
		throw new Error('Scans api not provided');
	}
	return api;
}

export function useOrdersApi() {
	const api = inject(ordersApiKey);

	if (!api) {
		throw new Error('Orders api not provided');
	}
	return api;
}

export function useLoyaltyApi() {
	const api = inject(loyaltyApiKey);

	if (!api) {
		throw new Error('Loyalty api not provided');
	}
	return api;
}

export function useVouchersApi() {
	const api = inject(vouchersApiKey);

	if (!api) {
		throw new Error('Vouchers api not provided');
	}
	return api;
}

export function useSweepstakeApi() {
	const api = inject(sweepstakeApiKey);

	if (!api) {
		throw new Error('Sweepstakes api not provided');
	}
	return api;
}
